<section class="w-full px-6 py-6" aria-label="Okta Admin">
  <!-- Top forms row -->
  <div class="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
    <!-- Onboard form -->
    <div>
      <div class="h-full bg-white p-8 shadow-sm">
        <h2 class="font-montserrat mb-6 text-center text-2xl text-blue-950">
          Onboard User to Okta
        </h2>

        <form
          [formGroup]="onboardingForm"
          (ngSubmit)="onSubmit()"
          class="flex flex-col gap-1"
        >
          <div>
            <label for="username" class="comcast-input-label">
              Username <span class="comcast-required-marker">*</span>
            </label>
            <input
              id="username"
              type="email"
              class="comcast-input"
              formControlName="username"
              placeholder="Enter username"
              aria-required="true"
            />
          </div>

          <div>
            <label for="email" class="comcast-input-label">
              Email <span class="comcast-required-marker">*</span>
            </label>
            <input
              id="email"
              type="email"
              class="comcast-input"
              formControlName="email"
              placeholder="Enter email address"
              aria-required="true"
              aria-describedby="onboarding-email-hint onboarding-email-error"
              [class.comcast-input-invalid]="
                onboardingForm.controls.email.touched &&
                onboardingForm.controls.email.invalid
              "
              [attr.aria-invalid]="
                onboardingForm.controls.email.touched &&
                onboardingForm.controls.email.invalid
              "
            />
            <p id="onboarding-email-hint" class="mt-1 text-xs text-gray-400">
              No public email domains (Gmail, Yahoo, Outlook, etc.).
            </p>
            @if (
              onboardingForm.controls.email.touched &&
              onboardingForm.controls.email.invalid
            ) {
              <div
                id="onboarding-email-error"
                role="alert"
                class="comcast-validation-error"
              >
                @if (onboardingForm.controls.email.hasError('required')) {
                  Email is required.
                } @else if (onboardingForm.controls.email.hasError('email')) {
                  Please enter a valid email address.
                } @else if (
                  onboardingForm.controls.email.hasError('publicEmailDomain')
                ) {
                  {{ publicEmailDomainError }}
                }
              </div>
            }
          </div>

          @if (formError()) {
            <div class="comcast-error-alert" role="alert">
              <mat-icon class="comcast-error-alert-icon" aria-hidden="true"
                >error</mat-icon
              >
              <p class="comcast-error-alert-text">{{ formError() }}</p>
            </div>
          }
          @if (formSuccess()) {
            <p class="comcast-validation-success" role="alert">
              <mat-icon
                class="h-5! w-5! text-xl! leading-none!"
                aria-hidden="true"
                >check_circle</mat-icon
              >
              {{ formSuccess() }}
            </p>
          }

          <div class="flex flex-col items-center gap-2 pt-2">
            <button
              type="button"
              class="btn-primary h-auto! min-h-12 w-full! max-w-full px-4 py-3 text-center leading-tight! tracking-[0.5px]! whitespace-normal! sm:w-fit! sm:min-w-76 sm:px-6"
              [disabled]="onboardingForm.invalid || isSubmitting()"
              (click)="onSubmitCustomActivationEmail()"
            >
              Send Activation Email
            </button>

            <button
              type="submit"
              class="font-lato cursor-pointer rounded text-sm font-semibold text-blue-600 hover:underline focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
              [disabled]="onboardingForm.invalid || isSubmitting()"
            >
              Send Default Okta Activation Email
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Federation check form -->
    <div>
      <div class="h-full bg-white p-8 shadow-sm">
        <h2 class="font-montserrat mb-6 text-center text-2xl text-blue-950">
          Look Up User in Okta
        </h2>

        <form
          [formGroup]="federationForm"
          (ngSubmit)="onLookUpUser()"
          class="flex flex-col gap-1"
        >
          <div>
            <label for="fed-username" class="comcast-input-label">
              Username <span class="comcast-required-marker">*</span>
            </label>
            <input
              id="fed-username"
              type="text"
              class="comcast-input"
              formControlName="username"
              placeholder="e.g. jfrick@supplylogic.com"
              aria-required="true"
            />
          </div>

          @if (federationError()) {
            <div class="comcast-error-alert" role="alert">
              <mat-icon class="comcast-error-alert-icon" aria-hidden="true"
                >error</mat-icon
              >
              <p class="comcast-error-alert-text">{{ federationError() }}</p>
            </div>
          }

          @if (existsError()) {
            <div class="comcast-error-alert" role="alert">
              <mat-icon class="comcast-error-alert-icon" aria-hidden="true"
                >error</mat-icon
              >
              <p class="comcast-error-alert-text">{{ existsError() }}</p>
            </div>
          }

          @if (existsResult(); as exists) {
            @let found = exists.existsInOkta;
            @let healthy = exists.existsInOkta && exists.inGroup;
            <section
              class="font-lato overflow-hidden rounded-lg border bg-white shadow-sm"
              [class]="
                healthy
                  ? 'border-green-200'
                  : found
                    ? 'border-amber-200'
                    : 'border-gray-200'
              "
              aria-label="Okta lookup result"
            >
              <!-- Result status -->
              <div
                class="flex items-center gap-2 px-4 py-2.5"
                [class]="
                  healthy
                    ? 'bg-green-50 text-green-800'
                    : found
                      ? 'bg-amber-50 text-amber-800'
                      : 'bg-gray-50 text-gray-600'
                "
              >
                <mat-icon
                  class="h-5! w-5! shrink-0 text-xl! leading-none!"
                  aria-hidden="true"
                  [class]="
                    healthy
                      ? 'text-green-600'
                      : found
                        ? 'text-amber-500'
                        : 'text-gray-400'
                  "
                >
                  {{ healthy ? 'check_circle' : found ? 'warning' : 'cancel' }}
                </mat-icon>
                <p class="text-sm font-semibold">
                  {{
                    healthy
                      ? 'Exists in Okta (in group)'
                      : found
                        ? 'Exists in Okta (not in group)'
                        : 'Not found in Okta'
                  }}
                </p>
              </div>

              @if (lookupUser(); as user) {
                <!-- Identity -->
                <div
                  class="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 pt-4"
                >
                  <div class="flex min-w-0 items-center gap-3">
                    <div
                      class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700"
                      aria-hidden="true"
                    >
                      {{ user.firstName | slice: 0 : 1
                      }}{{ user.lastName | slice: 0 : 1 }}
                    </div>
                    <div class="min-w-0">
                      <p class="truncate text-base font-semibold text-gray-900">
                        {{ user.firstName }} {{ user.lastName }}
                      </p>
                      <p class="truncate text-xs text-gray-500">
                        {{ user.login }}
                      </p>
                    </div>
                  </div>
                  <ng-container
                    *ngTemplateOutlet="
                      statusBadge;
                      context: { $implicit: user }
                    "
                  ></ng-container>
                </div>

                <!-- Details -->
                <dl
                  class="grid grid-cols-1 gap-x-6 gap-y-3 px-4 py-4 sm:grid-cols-2"
                >
                  <div class="min-w-0">
                    <dt
                      class="text-[11px] font-semibold tracking-wider text-gray-500 uppercase"
                    >
                      Email
                    </dt>
                    <dd class="mt-0.5 text-sm break-all text-gray-900">
                      {{ user.email || '—' }}
                    </dd>
                  </div>
                  <div>
                    <dt
                      class="text-[11px] font-semibold tracking-wider text-gray-500 uppercase"
                    >
                      Auth
                    </dt>
                    <dd class="mt-0.5 text-sm text-gray-900">
                      {{ user.authProvider || 'N/A' }}
                      @if (user.profile?.userType) {
                        <span class="text-gray-500"
                          >· {{ user.profile.userType }}</span
                        >
                      }
                    </dd>
                  </div>
                  <div>
                    <dt
                      class="text-[11px] font-semibold tracking-wider text-gray-500 uppercase"
                    >
                      Created
                    </dt>
                    <dd class="mt-0.5 text-sm text-gray-900">
                      {{ (user.created | date: 'MMM d, y, h:mm a') ?? 'N/A' }}
                    </dd>
                  </div>
                  <div>
                    <dt
                      class="text-[11px] font-semibold tracking-wider text-gray-500 uppercase"
                    >
                      Last login
                    </dt>
                    <dd class="mt-0.5 text-sm text-gray-900">
                      @if (user.lastLogin) {
                        {{ user.lastLogin | date: 'MMM d, y, h:mm a' }}
                      } @else {
                        <span class="text-amber-600">Never</span>
                      }
                    </dd>
                  </div>
                  <div>
                    <dt
                      class="text-[11px] font-semibold tracking-wider text-gray-500 uppercase"
                    >
                      Status changed
                    </dt>
                    <dd class="mt-0.5 text-sm text-gray-900">
                      {{
                        (user.statusChanged | date: 'MMM d, y, h:mm a') ?? '—'
                      }}
                    </dd>
                  </div>
                  <div>
                    <dt
                      class="text-[11px] font-semibold tracking-wider text-gray-500 uppercase"
                    >
                      Password set
                    </dt>
                    <dd class="mt-0.5 text-sm text-gray-900">
                      @if (user.passwordChanged) {
                        {{ user.passwordChanged | date: 'MMM d, y' }}
                      } @else {
                        <span class="text-amber-600">Never set</span>
                      }
                    </dd>
                  </div>
                  <div>
                    <dt
                      class="text-[11px] font-semibold tracking-wider text-gray-500 uppercase"
                    >
                      MFA
                    </dt>
                    <dd class="mt-0.5 text-sm">
                      <ng-container
                        *ngTemplateOutlet="
                          mfaCell;
                          context: { $implicit: user }
                        "
                      ></ng-container>
                    </dd>
                  </div>
                </dl>
              }

              <!-- Groups & apps -->
              @if (found && (exists.groups?.length || exists.apps?.length)) {
                <div
                  class="flex flex-col gap-3 border-t border-gray-100 px-4 py-3"
                >
                  @if (exists.groups?.length) {
                    <div>
                      <p
                        class="text-[11px] font-semibold tracking-wider text-gray-500 uppercase"
                      >
                        Groups
                      </p>
                      <div class="mt-1 flex flex-wrap gap-1.5">
                        @for (group of exists.groups; track group.id) {
                          <span
                            class="rounded-full border border-blue-100 bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700"
                            >{{ group.name }}</span
                          >
                        }
                      </div>
                    </div>
                  }
                  @if (exists.apps?.length) {
                    <div>
                      <p
                        class="text-[11px] font-semibold tracking-wider text-gray-500 uppercase"
                      >
                        Apps
                      </p>
                      <div class="mt-1 flex flex-wrap gap-1.5">
                        @for (app of exists.apps; track app.id) {
                          <span
                            class="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-700"
                            >{{ app.name }}</span
                          >
                        }
                      </div>
                    </div>
                  }
                </div>
              }

              <!-- Actions -->
              @if (lookupUser(); as user) {
                <div
                  class="flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-4 py-3"
                >
                  <span
                    class="text-[11px] font-semibold tracking-wider text-gray-500 uppercase"
                    >Actions</span
                  >
                  <ng-container
                    *ngTemplateOutlet="
                      userActions;
                      context: { $implicit: user, inGroup: exists.inGroup }
                    "
                  ></ng-container>
                </div>
              }
            </section>
          }

          @if (federationResult()) {
            @let result = federationResult()!;
            <div class="rounded border border-gray-200 bg-gray-50 p-4">
              <p class="font-lato text-sm font-semibold text-gray-700">
                Federation check
              </p>
              <ul class="mt-2 flex flex-col gap-0.5">
                @for (field of federationCheckFields; track field) {
                  @if (result[field] !== undefined) {
                    <li class="font-lato text-xs text-gray-600">
                      {{ field }}:
                      <span class="font-semibold">{{
                        result[field] ?? 'null'
                      }}</span>
                    </li>
                  }
                }
              </ul>

              @if (
                (result.existsInOkta ||
                  result.userFederated ||
                  (existsResult()?.groups?.length ?? 0) > 0) &&
                !result.inGroup
              ) {
                <div class="mt-3 border-t border-gray-200 pt-3">
                  @if (assignToGroupError()) {
                    <p class="font-lato mb-2 text-xs text-red-600" role="alert">
                      {{ assignToGroupError() }}
                    </p>
                  }
                  <button
                    type="button"
                    class="btn-primary w-full"
                    [disabled]="isAssigningToGroup()"
                    (click)="onAssignToGroup()"
                  >
                    @if (isAssigningToGroup()) {
                      <mat-progress-spinner
                        mode="indeterminate"
                        color="accent"
                        diameter="20"
                      ></mat-progress-spinner>
                      <span class="sr-only"
                        >Assigning to group, please wait</span
                      >
                    } @else {
                      Assign to Group
                    }
                  </button>
                </div>
              }
            </div>
          }

          <div class="flex justify-center pt-2">
            <button
              type="submit"
              class="btn-primary min-w-40"
              [disabled]="federationForm.invalid || isChecking()"
            >
              @if (isChecking()) {
                <mat-progress-spinner
                  mode="indeterminate"
                  color="accent"
                  diameter="20"
                ></mat-progress-spinner>
                <span class="sr-only">Looking up user, please wait</span>
              } @else {
                Look Up User
              }
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Make federated form -->
    <!-- <div>
      <div class="h-full bg-white p-8 shadow-sm">
        <h2 class="font-montserrat mb-6 text-center text-2xl text-blue-950">
          Convert or Delete Okta User
        </h2>

        <form
          [formGroup]="makeFederatedForm"
          (ngSubmit)="onMakeFederated()"
          class="flex flex-col gap-1"
        >
          <div>
            <label for="mf-username" class="comcast-input-label">
              Username <span class="comcast-required-marker">*</span>
            </label>
            <input
              id="mf-username"
              type="text"
              class="comcast-input"
              formControlName="username"
              placeholder="e.g. jfrick@supplylogic.com"
            />
          </div>

          @if (makeFederatedError()) {
            <div class="comcast-error-alert">
              <mat-icon class="comcast-error-alert-icon">error</mat-icon>
              <p class="comcast-error-alert-text">{{ makeFederatedError() }}</p>
            </div>
          }

          @if (makeFederatedResult(); as result) {
            @if (result.status === 'ok') {
              <div
                class="flex items-start gap-3 rounded border p-4"
                [class]="
                  result.code === 'ALREADY_FEDERATED'
                    ? 'border-amber-200 bg-amber-50'
                    : 'border-green-200 bg-green-50'
                "
              >
                <mat-icon
                  class="!h-5 !w-5 shrink-0 !text-[20px] leading-none!"
                  [class]="
                    result.code === 'ALREADY_FEDERATED'
                      ? 'text-amber-600'
                      : 'text-green-600'
                  "
                >
                  {{
                    result.code === 'ALREADY_FEDERATED'
                      ? 'info'
                      : 'check_circle'
                  }}
                </mat-icon>
                <div>
                  <p
                    class="font-lato text-sm font-semibold"
                    [class]="
                      result.code === 'ALREADY_FEDERATED'
                        ? 'text-amber-800'
                        : 'text-green-800'
                    "
                  >
                    {{ result.message }}
                  </p>
                  <p class="font-lato mt-1 text-xs font-semibold text-gray-600">
                    IDP: {{ result.data.idpName }}
                  </p>
                </div>
              </div>
            } @else {
              <div class="comcast-error-alert">
                <mat-icon class="comcast-error-alert-icon">error</mat-icon>
                <div>
                  <p class="comcast-error-alert-text">{{ result.message }}</p>
                  @if (result.hint) {
                    <p class="font-lato mt-0.5 text-xs text-gray-500">
                      {{ result.hint }}
                    </p>
                  }
                </div>
              </div>
            }
          }

          <div class="flex flex-wrap justify-center gap-3 pt-2">
            <button
              type="submit"
              class="btn-primary min-w-[10rem]"
              [disabled]="makeFederatedForm.invalid || isMakingFederated()"
            >
              @if (isMakingFederated()) {
                <mat-progress-spinner
                  mode="indeterminate"
                  color="accent"
                  diameter="20"
                ></mat-progress-spinner>
              } @else {
                Convert to Federated
              }
            </button>
            @if (!deleteConfirmPending()) {
              <button
                type="button"
                class="btn-secondary min-w-[10rem]"
                [disabled]="makeFederatedForm.invalid || isDeleting()"
                (click)="onDeleteUser()"
              >
                <mat-icon class="h-4! w-4! text-base! leading-none!"
                  >delete</mat-icon
                >
                Delete User
              </button>
            }
          </div>

          @if (deleteSuccess()) {
            <p class="comcast-validation-success text-sm">
              <mat-icon class="h-4! w-4! text-base! leading-none!"
                >check_circle</mat-icon
              >
              {{ deleteSuccess() }}
            </p>
          }

          @if (deleteError()) {
            <div class="comcast-error-alert">
              <mat-icon class="comcast-error-alert-icon">error</mat-icon>
              <p class="comcast-error-alert-text">{{ deleteError() }}</p>
            </div>
          }

          @if (deleteConfirmPending()) {
            <div class="rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
              <p class="font-semibold text-red-700">
                Delete "{{ makeFederatedForm.value.username }}" from Okta?
              </p>
              <p class="mt-0.5 text-xs text-red-500">
                This action cannot be undone.
              </p>
            </div>
            <div class="mt-3 flex justify-center gap-2">
              <button
                type="button"
                class="btn-secondary min-w-[7rem]"
                (click)="cancelDelete()"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn-danger min-w-[7rem]"
                [disabled]="isDeleting()"
                (click)="onConfirmDelete()"
              >
                @if (isDeleting()) {
                  <mat-progress-spinner
                    mode="indeterminate"
                    color="accent"
                    diameter="18"
                  ></mat-progress-spinner>
                } @else {
                  Confirm Delete
                }
              </button>
            </div>
          }
        </form>
      </div>
    </div> -->
  </div>
  <!-- end top forms grid -->

  <!-- Users table -->
  <div class="mt-8 border border-gray-200 bg-white pb-4 shadow-sm">
    <!-- Toolbar -->
    <div
      class="flex flex-wrap items-center justify-between gap-3 bg-gray-50 px-6 py-4"
    >
      <div class="flex items-center gap-2">
        <span class="font-boldfix text-lg uppercase">Okta Users</span>
        @if (!isLoading() && !errorMessage()) {
          <span class="font-normal text-gray-400 lowercase">
            ({{ dataSource.filteredData.length }} of
            {{ dataSource.data.length }} loaded)
          </span>
        }
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <!-- Search field selector + input -->
        <div class="flex">
          <select
            aria-label="Select search field type"
            [(ngModel)]="searchField"
            (change)="applyFilter()"
            class="comcast-select mt-0! h-12! w-auto! rounded-r-none border-r-0 py-2! text-sm!"
          >
            <option value="all">All fields</option>
            <option value="login">Login</option>
            <option value="email">Email</option>
            <option value="name">Name</option>
          </select>
          <div class="relative w-64">
            <input
              type="text"
              aria-label="Search users"
              [placeholder]="
                searchField === 'login'
                  ? 'Search by login...'
                  : searchField === 'email'
                    ? 'Search by email...'
                    : searchField === 'name'
                      ? 'Search by name...'
                      : 'Search users...'
              "
              class="comcast-input mt-0! h-12! rounded-l-none pr-8 pl-3"
              [(ngModel)]="searchTerm"
              (ngModelChange)="applyFilter()"
            />
            @if (searchTerm) {
              <button
                (click)="clearSearch()"
                aria-label="Clear search"
                class="absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-1"
              >
                <mat-icon
                  class="flex! h-4! w-4! items-center! justify-center! text-base!"
                  aria-hidden="true"
                  >close</mat-icon
                >
              </button>
            }
          </div>
        </div>

        <!-- Status filter -->
        <div class="relative">
          <button
            type="button"
            aria-label="Filter by status"
            class="comcast-select-trigger min-h-12 min-w-40 py-2! text-sm!"
            [class.comcast-select-trigger--open]="isStatusFilterOpen()"
            [class.comcast-select-trigger--filled]="!!statusFilter.length"
            [attr.aria-expanded]="isStatusFilterOpen()"
            aria-haspopup="listbox"
            aria-controls="status-filter-listbox"
            (click)="toggleStatusFilterDropdown($event)"
          >
            <span>{{ selectedStatusFilterLabel }}</span>
            <mat-icon class="comcast-select-icon" aria-hidden="true">
              {{ isStatusFilterOpen() ? 'arrow_drop_up' : 'arrow_drop_down' }}
            </mat-icon>
          </button>

          @if (isStatusFilterOpen()) {
            <div
              id="status-filter-listbox"
              class="comcast-select-panel"
              [class.comcast-select-panel--open]="isStatusFilterOpen()"
              role="listbox"
              aria-label="Filter by status"
              aria-multiselectable="true"
              (click)="$event.stopPropagation()"
            >
              <div class="comcast-select-options">
                <button
                  type="button"
                  class="comcast-select-option w-full text-left"
                  [class.comcast-select-option--selected]="!statusFilter.length"
                  role="option"
                  [attr.aria-selected]="!statusFilter.length"
                  (click)="clearStatusFilter($event)"
                >
                  All statuses
                </button>
                @for (entry of statusOptions; track entry[0]) {
                  <button
                    type="button"
                    class="comcast-select-option flex w-full items-center gap-2 text-left"
                    [class.comcast-select-option--selected]="
                      isStatusSelected(entry[0])
                    "
                    role="option"
                    [attr.aria-selected]="isStatusSelected(entry[0])"
                    (click)="toggleStatusFilter(entry[0], $event)"
                  >
                    <span
                      class="flex h-4 w-4 items-center justify-center rounded border"
                      [ngClass]="
                        isStatusSelected(entry[0])
                          ? 'border-blue-600 bg-blue-600 text-white'
                          : 'border-gray-400 bg-white text-transparent'
                      "
                      aria-hidden="true"
                    >
                      <mat-icon
                        class="flex! h-3! w-3! items-center! justify-center! text-xs!"
                        >check</mat-icon
                      >
                    </span>
                    <span>{{ entry[1].label }}</span>
                  </button>
                }
              </div>
            </div>
          }
        </div>

        <!-- Auth provider filter -->
        <select
          aria-label="Filter by auth provider"
          [ngModel]="authProviderFilter()"
          (ngModelChange)="authProviderFilter.set($event); applyFilter()"
          class="comcast-select mt-0! h-12! w-auto! py-2! text-sm!"
        >
          <option value="">All auth types</option>
          <option value="FEDERATION">Federated (SSO)</option>
          <option value="OKTA">Local (Okta)</option>
        </select>

        <button
          type="button"
          (click)="clearFilters()"
          class="font-lato h-12 items-center justify-center gap-2 px-4 text-sm font-semibold text-blue-600 transition-colors hover:border-blue-200 hover:bg-blue-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
          [disabled]="
            !searchTerm &&
            !statusFilter.length &&
            searchField === 'all' &&
            !authProviderFilter()
          "
        >
          Clear filters
          <mat-icon
            class="h-4! w-4! text-base! leading-none!"
            aria-hidden="true"
            >close</mat-icon
          >
        </button>
      </div>
    </div>

    <!-- Loading -->
    @if (isLoading()) {
      <div
        class="flex flex-col items-center justify-center py-16 text-gray-400"
        role="status"
        aria-live="polite"
      >
        <mat-progress-spinner
          mode="indeterminate"
          diameter="36"
          color="primary"
        ></mat-progress-spinner>
        <p class="mt-4 text-sm">Loading users...</p>
      </div>
    }

    <!-- Error -->
    @if (errorMessage()) {
      <div
        class="mx-6 mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        role="alert"
      >
        <div class="flex items-center gap-2">
          <mat-icon
            class="h-4! w-4! text-base! leading-none!"
            aria-hidden="true"
            >error_outline</mat-icon
          >
          {{ errorMessage() }}
        </div>
      </div>
    }

    <!-- Table -->
    <div [hidden]="isLoading() || !!errorMessage()">
      <div class="overflow-x-auto">
        <table
          mat-table
          [dataSource]="dataSource"
          matSort
          matSortActive="user"
          matSortDirection="asc"
          class="w-full"
          aria-label="Okta users"
        >
          <!-- Avatar -->
          <ng-container matColumnDef="avatar">
            <th
              mat-header-cell
              *matHeaderCellDef
              class="w-14! py-3! pr-3! pl-6!"
              aria-label="Avatar"
            ></th>
            <td mat-cell *matCellDef="let user" class="w-14! py-3! pr-3! pl-6!">
              <div
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-600"
                aria-hidden="true"
              >
                {{ user.firstName | slice: 0 : 1
                }}{{ user.lastName | slice: 0 : 1 }}
              </div>
            </td>
          </ng-container>

          <!-- User (name) -->
          <ng-container matColumnDef="user">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="font-montserrat px-3! py-3! text-left text-sm font-bold tracking-widest uppercase"
            >
              User
            </th>
            <td mat-cell *matCellDef="let user" class="px-3! py-3!">
              <div class="font-medium text-gray-800">
                {{ user.lastName }}, {{ user.firstName }}
              </div>
            </td>
          </ng-container>

          <!-- Email -->
          <ng-container matColumnDef="email">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="font-montserrat px-3! py-3! text-left text-sm font-bold tracking-widest uppercase"
            >
              Email
            </th>
            <td mat-cell *matCellDef="let user" class="px-3! py-3!">
              <span class="text-sm text-gray-600">{{ user.email }}</span>
            </td>
          </ng-container>

          <!-- Login -->
          <ng-container matColumnDef="login">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="font-montserrat px-3! py-3! text-left text-sm font-bold tracking-widest uppercase"
            >
              Login
            </th>
            <td mat-cell *matCellDef="let user" class="px-3! py-3!">
              <span class="text-sm text-blue-600">{{ user.login }}</span>
            </td>
          </ng-container>

          <!-- Status -->
          <ng-container matColumnDef="status">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="font-montserrat px-3! py-3! text-left text-sm font-bold tracking-widest uppercase"
            >
              Status
            </th>
            <td mat-cell *matCellDef="let user" class="px-3! py-3!">
              <ng-container
                *ngTemplateOutlet="statusBadge; context: { $implicit: user }"
              ></ng-container>
            </td>
          </ng-container>

          <!-- Auth Provider -->
          <ng-container matColumnDef="authProvider">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="font-montserrat px-3! py-3! text-left text-sm font-bold tracking-widest uppercase"
            >
              Auth
            </th>
            <td mat-cell *matCellDef="let user" class="px-3! py-3!">
              <span
                class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium"
                [ngClass]="
                  user.authProvider === 'FEDERATION'
                    ? 'border-purple-200 bg-purple-50 text-purple-700'
                    : 'border-gray-200 bg-gray-50 text-gray-600'
                "
              >
                {{ user.authProvider || 'N/A' }}:
                {{ user.profile?.userType || '' }}
              </span>
            </td>
          </ng-container>

          <!-- Last Login -->
          <ng-container matColumnDef="lastLogin">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="font-montserrat w-36! px-3! py-3! text-left text-sm font-bold tracking-widest uppercase"
            >
              Last Login
            </th>
            <td mat-cell *matCellDef="let user" class="w-36! px-3! py-3!">
              @if (user.lastLogin) {
                <span class="text-xs text-gray-600">{{
                  user.lastLogin | date: 'MMM d, y'
                }}</span>
                <div class="text-[11px] text-gray-400">
                  {{ user.lastLogin | date: 'h:mm a' }}
                </div>
              } @else {
                <span class="text-xs text-amber-500 italic">Never</span>
              }
            </td>
          </ng-container>

          <!-- Created -->
          <ng-container matColumnDef="created">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="font-montserrat w-36! px-3! py-3! text-left text-sm font-bold tracking-widest uppercase"
            >
              Created Date
            </th>
            <td mat-cell *matCellDef="let user" class="w-36! px-3! py-3!">
              @if (user.created) {
                <span class="text-xs text-gray-600">{{
                  user.created | date: 'MMM d, y'
                }}</span>
                <div class="text-[11px] text-gray-400">
                  {{ user.created | date: 'h:mm a' }}
                </div>
              } @else {
                <span class="text-xs text-gray-400 italic">N/A</span>
              }
            </td>
          </ng-container>

          <!-- Status Changed -->
          <ng-container matColumnDef="statusChanged">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="font-montserrat w-36! px-3! py-3! text-left text-sm font-bold tracking-widest uppercase"
            >
              Status Changed
            </th>
            <td mat-cell *matCellDef="let user" class="w-36! px-3! py-3!">
              @if (user.statusChanged) {
                <span class="text-xs text-gray-600">{{
                  user.statusChanged | date: 'MMM d, y'
                }}</span>
                <div class="text-[11px] text-gray-400">
                  {{ user.statusChanged | date: 'h:mm a' }}
                </div>
              } @else {
                <span class="text-xs text-gray-400 italic">—</span>
              }
            </td>
          </ng-container>

          <!-- Password Changed -->
          <ng-container matColumnDef="passwordChanged">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="font-montserrat w-36! px-3! py-3! text-left text-sm font-bold tracking-widest uppercase"
            >
              Password Set
            </th>
            <td mat-cell *matCellDef="let user" class="w-36! px-3! py-3!">
              @if (user.passwordChanged) {
                <span class="text-xs text-gray-600">{{
                  user.passwordChanged | date: 'MMM d, y'
                }}</span>
              } @else {
                <span class="text-xs text-amber-500 italic">Never set</span>
              }
            </td>
          </ng-container>

          <!-- MFA -->
          <ng-container matColumnDef="mfa">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="font-montserrat px-3! py-3! text-left text-sm font-bold tracking-widest uppercase"
            >
              MFA
            </th>
            <td mat-cell *matCellDef="let user" class="px-3! py-3!">
              <ng-container
                *ngTemplateOutlet="mfaCell; context: { $implicit: user }"
              ></ng-container>
            </td>
          </ng-container>

          <!-- Actions -->
          <ng-container matColumnDef="actions">
            <th
              mat-header-cell
              *matHeaderCellDef
              class="w-28! py-3! pr-6! pl-3!"
              aria-label="Actions"
            ></th>
            <td mat-cell *matCellDef="let user" class="w-28! py-3! pr-6! pl-3!">
              <ng-container
                *ngTemplateOutlet="
                  userActions;
                  context: { $implicit: user, inGroup: true }
                "
              ></ng-container>
            </td>
          </ng-container>

          <tr
            mat-header-row
            *matHeaderRowDef="displayedColumns"
            class="border-b border-gray-100 bg-white"
          ></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns"
            class="border-b border-gray-100 transition-colors hover:bg-gray-50"
          ></tr>

          <tr class="mat-row" *matNoDataRow>
            <td
              class="mat-cell px-4 py-10 text-center text-sm text-gray-400"
              [attr.colspan]="displayedColumns.length"
              role="status"
              aria-live="polite"
            >
              @if (searchTerm || statusFilter.length) {
                No users match your search or filter.
              } @else {
                No users found.
              }
            </td>
          </tr>
        </table>
      </div>

      <!-- Server pages: loaded only on request, never all at once -->
      @if (hasMore() || isLoadingMore() || loadMoreError()) {
        <div
          class="flex flex-wrap items-center justify-center gap-3 px-6 pt-4 text-sm"
        >
          <span class="text-gray-500">
            Search, filters and sorting apply to loaded users only.
          </span>
          <button
            type="button"
            (click)="loadMore()"
            [disabled]="isLoadingMore()"
            class="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-1.5 font-bold text-blue-600 transition-colors hover:border-blue-300 hover:bg-blue-50 focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
          >
            @if (isLoadingMore()) {
              <mat-progress-spinner
                mode="indeterminate"
                diameter="14"
                aria-hidden="true"
              ></mat-progress-spinner>
              Loading…
            } @else if (loadMoreError()) {
              Retry loading more users
            } @else {
              Load more users
            }
          </button>
          @if (loadMoreError(); as error) {
            <span class="text-red-600" role="alert">{{ error }}</span>
          }
        </div>
      }

      <mat-paginator
        [pageSizeOptions]="pageSizeOptions"
        [pageSize]="defaultPageSize"
        showFirstLastButtons
        aria-label="Select page of users"
      ></mat-paginator>
    </div>
  </div>
</section>

<ng-template #userActions let-user let-inGroup="inGroup">
  <div class="flex items-center justify-end gap-1">
    <!-- Resend invite: PROVISIONED, STAGED -->
    @if (canResendInvite(user)) {
      <button
        type="button"
        (click)="resendInvite(user)"
        [attr.aria-label]="
          'Resend invite for ' + user.firstName + ' ' + user.lastName
        "
        matTooltip="Resend invitation"
        class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-400 transition-colors hover:border-blue-300 hover:text-blue-600 focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-1"
      >
        <mat-icon class="h-4! w-4! text-base! leading-none!" aria-hidden="true"
          >mail</mat-icon
        >
      </button>
    }

    <!-- Resend branded activation: local users with PROVISIONED, STAGED -->
    @if (canResendCustomActivation(user)) {
      <button
        type="button"
        (click)="resendCustomActivation(user)"
        [disabled]="isResendingCustomActivation(user)"
        [attr.aria-label]="
          'Resend branded activation email for ' +
          user.firstName +
          ' ' +
          user.lastName
        "
        matTooltip="Resend branded activation"
        class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-400 transition-colors hover:border-blue-300 hover:text-blue-600 focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <mat-icon class="h-4! w-4! text-base! leading-none!" aria-hidden="true"
          >mark_email_read</mat-icon
        >
      </button>
    }

    <!-- TODO: Restore when reset-password permission is approved. -->
    <!-- Reset password: OKTA users with ACTIVE, PASSWORD_EXPIRED -->
    <!-- @if (canResetPassword(user)) {
      <button
        type="button"
        (click)="resetPassword(user)"
        [attr.aria-label]="
          'Send password reset for ' +
          user.firstName +
          ' ' +
          user.lastName
        "
        matTooltip="Send password reset"
        class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-400 transition-colors hover:border-blue-300 hover:text-blue-600"
      >
        <mat-icon
          class="h-4! w-4! text-base! leading-none!"
          aria-hidden="true"
          >lock_reset</mat-icon
        >
      </button>
    } -->

    <!-- Unlock: LOCKED_OUT, RECOVERY -->
    @if (canUnlock(user)) {
      <button
        type="button"
        (click)="unlockUser(user)"
        [attr.aria-label]="'Unlock ' + user.firstName + ' ' + user.lastName"
        matTooltip="Unlock User"
        class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-amber-500 transition-colors hover:border-amber-300 hover:bg-amber-50 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1"
      >
        <mat-icon class="h-4! w-4! text-base! leading-none!" aria-hidden="true"
          >lock_open</mat-icon
        >
      </button>
    }

    <!-- Reset MFA: users with enrolled factors -->
    @if (canResetMfa(user)) {
      <button
        type="button"
        (click)="resetMfa(user)"
        [attr.aria-label]="
          'Reset MFA for ' + user.firstName + ' ' + user.lastName
        "
        matTooltip="Reset MFA"
        class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-blue-600 transition-colors hover:border-blue-300 hover:bg-blue-50 focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-1"
      >
        <mat-icon class="h-4! w-4! text-base! leading-none!" aria-hidden="true"
          >phonelink_erase</mat-icon
        >
      </button>
    }

    <!-- Reactivate: SUSPENDED, DEPROVISIONED -->
    @if (canReactivate(user)) {
      <button
        type="button"
        (click)="reactivateUser(user)"
        [attr.aria-label]="'Reactivate ' + user.firstName + ' ' + user.lastName"
        matTooltip="Reactivate user"
        class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-400 transition-colors hover:border-green-300 hover:text-green-600 focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-1"
      >
        <mat-icon class="h-4! w-4! text-base! leading-none!" aria-hidden="true"
          >how_to_reg</mat-icon
        >
      </button>
    }

    <!-- Remove from portal group: federated users in the group; all except SUSPENDED, DEPROVISIONED -->
    @if (
      inGroup && user.authProvider === 'FEDERATION' && canSuspend(user.status)
    ) {
      <button
        type="button"
        (click)="deactivateOktaUser(user)"
        [attr.aria-label]="
          'Remove ' +
          user.firstName +
          ' ' +
          user.lastName +
          ' from portal group'
        "
        matTooltip="Remove from portal group"
        class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-amber-500 transition-colors hover:border-amber-300 hover:bg-amber-50 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1"
      >
        <mat-icon class="h-4! w-4! text-base! leading-none!" aria-hidden="true"
          >group_remove</mat-icon
        >
      </button>
    }

    <!-- Deactivate: local users in the group; all except SUSPENDED, DEPROVISIONED -->
    @if (
      inGroup && user.authProvider !== 'FEDERATION' && canSuspend(user.status)
    ) {
      <button
        type="button"
        (click)="deactivateOktaUser(user)"
        [attr.aria-label]="'Deactivate ' + user.firstName + ' ' + user.lastName"
        matTooltip="Deactivate user"
        class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-amber-500 transition-colors hover:border-amber-300 hover:bg-amber-50 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1"
      >
        <mat-icon class="h-4! w-4! text-base! leading-none!" aria-hidden="true"
          >person_off</mat-icon
        >
      </button>
    }

    <!-- Delete (hard delete): local users only, all except SUSPENDED, DEPROVISIONED -->
    @if (user.authProvider !== 'FEDERATION') {
      <button
        type="button"
        (click)="suspendUser(user)"
        [attr.aria-label]="'Remove ' + user.firstName + ' ' + user.lastName"
        matTooltip="Remove/hard delete user"
        class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-red-500 transition-colors hover:border-red-300 hover:bg-red-50 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
      >
        <mat-icon class="h-4! w-4! text-base! leading-none!" aria-hidden="true"
          >delete</mat-icon
        >
      </button>
    }
  </div>
</ng-template>

<ng-template #statusBadge let-user>
  <div class="flex items-center gap-2">
    <span
      class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium"
      [ngClass]="getStatusConfig(user.status).classes"
    >
      <span
        class="h-1.5 w-1.5 rounded-full"
        [ngClass]="getStatusConfig(user.status).dot"
        aria-hidden="true"
      ></span>
      {{ getStatusConfig(user.status).label }}
    </span>
    @if (user.cannotLogin && user.loginIssue) {
      <mat-icon
        class="h-4! w-4! text-base! leading-none! text-amber-500"
        [matTooltip]="user.loginIssue.reason"
        role="img"
        [attr.aria-label]="'Login issue: ' + user.loginIssue.reason"
        >warning</mat-icon
      >
    }
  </div>
</ng-template>

<ng-template #mfaCell let-user>
  @if (user.mfa?.enrolled) {
    <div
      class="flex flex-wrap gap-1"
      [matTooltip]="getMfaTooltip(user)"
      matTooltipClass="whitespace-pre"
    >
      @for (factor of user.mfa.factors; track factor.type) {
        <span
          class="inline-block rounded bg-blue-50 px-1.5 py-0.5 text-sm font-bold text-blue-600 uppercase"
        >
          {{ factor.provider }}
        </span>
      }
    </div>
  } @else if (user.mfa && !user.mfa.enrolled) {
    <span class="text-xs text-amber-600">Not enrolled</span>
  } @else if (isMfaLoading(user)) {
    <mat-progress-spinner
      mode="indeterminate"
      diameter="16"
      [attr.aria-label]="
        'Loading MFA for ' + user.firstName + ' ' + user.lastName
      "
    ></mat-progress-spinner>
  } @else {
    <button
      type="button"
      (click)="loadMfa(user)"
      [attr.aria-label]="
        (mfaError(user) ? 'Retry loading MFA for ' : 'Check MFA for ') +
        user.firstName +
        ' ' +
        user.lastName
      "
      [matTooltip]="mfaError(user) ?? ''"
      class="cursor-pointer rounded text-xs font-bold text-blue-600 hover:underline focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-1"
      [class.text-red-600]="!!mfaError(user)"
    >
      {{ mfaError(user) ? 'Retry' : 'Check' }}
    </button>
  }
</ng-template>
